import React from "react";
import {
  TextField,
  styled,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  IconButton,
  Box,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { SelectField } from "../../../common/Controls";
import { SelectFieldProps } from "../../../common/Controls/SelectField";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { Delete } from "@mui/icons-material";

export type NonEmptyArray<T> = {
  0: T;
} & Array<T>;
type FieldRowProps = {
  fields: NonEmptyArray<FieldProps>;
  columns: number;
};
export type FormProps = {
  fields: NonEmptyArray<FieldProps>[];
};

type TypedFieldProps<T, K = T> = {
  label: string;
  onChange: (value?: T) => void;
  value: K;
  required?: boolean;
  disabled?: boolean;
};
type FormValues<T> = {
  name: T;
  phone: T;
};

type ListItemFormProps<T, O> = {
  labelfield: string[];
  required?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
  value: T[];
  fieldvalue: FormValues<T>;
  onChange: (values: Record<string, O>) => void;
  item: (item: T) => T;
  onSelect: (item?: T) => void;
};
export type FieldProps =
  | ({ type: "text" } & TypedFieldProps<string>)
  | ({ type: "list" } & ListItemFormProps<any, any>)
  | ({ type: "select" } & SelectFieldProps)
  | ({ type: "password" } & TypedFieldProps<string>)
  | ({ type: "number" } & TypedFieldProps<string, string | number>)
  | ({ type: "date" } & TypedFieldProps<number | undefined, number | null>);

const Field = (props: FieldProps) => {
  if (props === undefined) return <></>;
  switch (props.type) {
    case "select":
      return <SelectField fullWidth {...props} />;
    case "text":
      return (
        <TextField
          fullWidth
          {...props}
          type="text"
          onChange={(evt) => props.onChange(evt.target.value)}
        />
      );
    case "list":
      return (
        <Grid
          container
          direction="row"
          sx={{ width: '100%', gap: 1,
            flexBasis: "100%", }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              flex: "1",
            }}
          >
            <TextField
              fullWidth
              label={props.labelfield[0]}
              type="text"
              required={props.value.length > 0 ? false : true}
              onChange={(evt) => {
                props.onChange({
                  name: evt.target.value,
                  phone: props.fieldvalue.phone,
                });
              }}
              value={props.fieldvalue.name}
            />
            <TextField
              fullWidth
              required={props.value.length > 0 ? false : true}
              label={props.labelfield[1]}
              type="text"
              onChange={(evt) => {
                props.onChange({
                  name: props.fieldvalue.name,
                  phone: evt.target.value,
                });
              }}
              value={props.fieldvalue.phone}
            />
            {props.children}
          </Box>
          <Grid
            sx={{
              flex: "1",
            }}
          >
            <StyledList>
              {props.value?.map((item, index) => (
                <ListItem
                  key={index}
                  secondaryAction={
                    <IconButton onClick={() => props.onSelect(item)}>
                      <Delete />
                    </IconButton>
                  }
                  disablePadding
                >
                  <ListItemButton dense>
                    <ListItemText primary={props.item(item)} />
                  </ListItemButton>
                </ListItem>
              ))}
            </StyledList>
          </Grid>
        </Grid>
      );
    case "password":
      return (
        <TextField
          fullWidth
          {...props}
          autoComplete="new-password"
          type="password"
          onChange={(evt) => props.onChange(evt.target.value)}
        />
      );
    case "number":
      return (
        <TextField
          fullWidth
          {...props}
          type="text"
          onChange={(evt) => props.onChange(evt.target.value)}
          slotProps={{
            htmlInput: { inputMode: "numeric", pattern: "[0-9]*" }
          }}
        />
      );
    case "date":
      return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DateTimePicker
            label={props.label}
            slotProps={{
              textField: {
                required: props.required,
                InputLabelProps: { shrink: true },
                fullWidth: true,
              },
            }}
            onChange={(date: Date | null) =>
              props.onChange(date?.getTime() ?? undefined)
            }
            value={props.value ? new Date(props.value) : null}
          />
        </LocalizationProvider>
      );
  }
};

const RowContainer = styled(Grid)(({ theme }) => ({
  "& > .MuiGrid2-root": {
    "&:has(*)": { margin: theme.spacing(0.625) },
    margin: theme.spacing(0, 0.625),
    minWidth: "20ch",
  },
}));
const StyledList = styled(List)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  maxHeight: "10em",
  minHeight: "10em",
  overflow: "auto",
  paddingTop: "0px",
}));

const FieldRow = (props: FieldRowProps) => {
  const { fields, columns } = props;
  const padded: (FieldProps | undefined)[] = [...fields];
  padded.length = columns;
  padded.fill(undefined, fields.length);

  return (
    <RowContainer container direction="row">
      {padded.map((fieldProps, index) => (
        <Grid
          key={index}
          sx={{
            flex: 1,
          }}
        >
          {fieldProps && <Field {...fieldProps} />}
        </Grid>
      ))}
    </RowContainer>
  );
};

const Form = (props: FormProps) => {
  const { fields } = props;
  const columns = fields.reduce((acc, arr) => Math.max(acc, arr.length), 0);
  return (
    <>
      {fields.map((fields, index) => (
        <FieldRow key={index} fields={fields} columns={columns} />
      ))}
    </>
  );
};

export default Form;
